import { getCookie, getReferrerUrl, setCookie } from "../components/Hooks";
import { applyAffiliateTouch, AFFILIATE_UTM_KEYS, type AffiliateTouch } from "./affiliateTouch";

/**
 * Attribution capture.
 *
 * This used to live in a `React.useEffect(..., [])` inside `Inputs/Button.tsx`,
 * so it only ran when a Button happened to mount. That was unreliable for two
 * reasons:
 *  - The CookieYes CMP wipes non essential cookies (`entryPage`, `referrerUrl`,
 *    `gtf_campaign`, utm_*) while consent has not been given, and nothing
 *    rewrote them after the user accepted, so the landing page was lost.
 *  - Pages without a Button never captured anything.
 *
 * EVERY captured value (click ids, utm_*, tid, ucc, entryPage, referrerUrl) is
 * additionally sealed in `sessionStorage`, which the CMP does not touch, so the
 * attribution of the session survives a cookie wipe. Before 2026-09-15 only
 * `entryPage` / `referrerUrl` were sealed, so a visitor who had not yet
 * answered the banner lost `gclid` & friends for the whole session and the
 * handoff to the app arrived undecorated.
 *
 * CookieYes configuration note (CMP side, not in this repo — see gatsby-ssr.js):
 * `gclid`, `gbraid`, `wbraid`, `msclkid` and the `utm_*` cookies should be
 * classified as *necessary* in the CookieYes cookie list so the CMP stops
 * deleting them. The seal below is the belt; that classification is the braces.
 */

const SESSION_ENTRY_PAGE_KEY = "l5_entry_page";
const SESSION_REFERRER_URL_KEY = "l5_referrer_url";
/** One key per attribution param, e.g. `l5_attr_gclid`. */
const SESSION_PARAM_PREFIX = "l5_attr_";

// Same list (and same order) the Button effect used.
export const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "tid",
  "utm_content",
  "utm_term",
  "utm_test",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "externalReference",
  "ucc",
];

// Click ids live longer than the default cookie expiry (3 months).
const LONG_LIVED_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "ucc",
];

/** The two landing values, sealed under their own (legacy) session keys. */
export const LANDING_PARAMS = ["entryPage", "referrerUrl"];

let attributionCaptureInstalled = false;

/**
 * `getCookie` reports an empty cookie as the string "defined" and a cookie
 * literally holding "null" as `null`; neither is a value worth carrying.
 */
export const isUsableAttributionValue = (
  value: string | null | undefined
): value is string => !!value && value !== "defined" && value !== "null";

const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    // Safari private mode / storage disabled.
    return null;
  }
};

const writeSession = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    // Ignore: attribution still works through cookies.
  }
};

const removeSession = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // Ignore.
  }
};

const sessionKeyFor = (name: string): string => {
  if (name === "entryPage") return SESSION_ENTRY_PAGE_KEY;
  if (name === "referrerUrl") return SESSION_REFERRER_URL_KEY;
  return `${SESSION_PARAM_PREFIX}${name}`;
};

const expiryFor = (name: string): Date | undefined =>
  LONG_LIVED_PARAMS.includes(name)
    ? new Date(new Date().setMonth(new Date().getMonth() + 3))
    : undefined;

const writeCookie = (name: string, value: string) => {
  const expires = expiryFor(name);
  if (expires) setCookie(name, value, expires);
  else setCookie(name, value);
};

/**
 * An anchor with a full url (https://life5.es/calcular-seguro-vida/) instead of
 * a pathname makes the browser report our own site as the referrer. Those must
 * never be stored as `referrerUrl`.
 */
const isOwnSiteReferrer = (referrer: string) => {
  if (!referrer) return false;

  const siteUrl = process.env.GATSBY_SITE_URL;
  if (siteUrl && referrer.includes(siteUrl)) return true;

  const domainUrl = process.env.GATSBY_DOMAIN_URL;
  if (domainUrl) {
    try {
      const { hostname } = new URL(referrer);
      const bareDomain = domainUrl.replace(/^\./, "");
      if (hostname === bareDomain || hostname.endsWith(`.${bareDomain}`)) {
        return true;
      }
    } catch (error) {
      // Malformed referrer: fall through, treat it as external.
    }
  }

  return false;
};

const getExternalReferrer = () => {
  const referrer = typeof document !== "undefined" ? document.referrer : "";
  return referrer && !isOwnSiteReferrer(referrer) ? referrer : "";
};

/** Stores the landing values once per session (the first run wins). */
const sealLandingValues = () => {
  if (readSession(SESSION_ENTRY_PAGE_KEY) !== null) return;
  writeSession(SESSION_ENTRY_PAGE_KEY, window.location.href);
  writeSession(SESSION_REFERRER_URL_KEY, getExternalReferrer());
};

/** A new campaign arrived: the landing values are re-sealed on purpose. */
const saveNewLandingValues = () => {
  const entryPage = window.location.href;
  const referrerUrl = getExternalReferrer();

  writeSession(SESSION_ENTRY_PAGE_KEY, entryPage);
  writeSession(SESSION_REFERRER_URL_KEY, referrerUrl);

  setCookie("entryPage", entryPage);
  if (referrerUrl) setCookie("referrerUrl", referrerUrl);
};

/**
 * Rewrites the cookie only when it is missing (or empty, which `getCookie`
 * reports as "defined"). An existing `entryPage` is never overwritten outside
 * of a new campaign arrival.
 */
const restoreEntryPageCookie = () => {
  const entryPage = getCookie("entryPage");
  if (entryPage !== null && entryPage !== "defined") return;
  setCookie(
    "entryPage",
    readSession(SESSION_ENTRY_PAGE_KEY) || window.location.href
  );
};

const restoreReferrerUrlCookie = () => {
  if (getReferrerUrl() !== null) return;
  const sealed = readSession(SESSION_REFERRER_URL_KEY);
  const referrerUrl = sealed !== null ? sealed : getExternalReferrer();
  // Nothing to store when the session started without an external referrer.
  if (!referrerUrl) return;
  setCookie("referrerUrl", referrerUrl);
};

/**
 * Landing values sealed for this session, for callers that need them even
 * when the CMP has deleted the cookies (e.g. the handoff to the app).
 */
export const getSealedLandingValues = (): {
  entryPage: string | null;
  referrerUrl: string | null;
} => {
  if (typeof window === "undefined")
    return { entryPage: null, referrerUrl: null };
  return {
    entryPage: readSession(SESSION_ENTRY_PAGE_KEY),
    referrerUrl: readSession(SESSION_REFERRER_URL_KEY) || null,
  };
};

/** The sealed value of a single attribution key (no cookie lookup). */
export const getSealedAttributionValue = (name: string): string | null => {
  if (typeof window === "undefined") return null;
  const value = readSession(sessionKeyFor(name));
  return isUsableAttributionValue(value) ? value : null;
};

/**
 * THE getter every consumer should use: the cookie when the CMP has left it
 * alone, otherwise the value sealed in sessionStorage when the session started.
 */
export const getAttributionValue = (name: string): string | null => {
  if (typeof window === "undefined") return null;
  const cookie = name === "referrerUrl" ? getReferrerUrl() : getCookie(name);
  if (isUsableAttributionValue(cookie)) return cookie;
  return getSealedAttributionValue(name);
};

/** Every attribution key available for this session, cookie or seal. */
export const getAllAttributionValues = (): Record<string, string> => {
  const out: Record<string, string> = {};
  ATTRIBUTION_PARAMS.concat(LANDING_PARAMS).forEach((name) => {
    const value = getAttributionValue(name);
    if (value) out[name] = value;
  });
  return out;
};

const recoverParam = (
  query: URLSearchParams,
  name: string,
  expires?: Date
) => {
  const value = query.get(name);
  setCookie(name, value !== "null" && value !== null ? value : null, expires);
};

/**
 * Makes cookies and seal agree:
 *  - a cookie that survived is (re)sealed, so a later CMP wipe cannot lose it;
 *  - a cookie the CMP already deleted is rewritten from the seal.
 * Runs on every capture, i.e. also on the `cookieyes_consent_update` event.
 */
const syncAttributionSeal = () => {
  ATTRIBUTION_PARAMS.concat(LANDING_PARAMS).forEach((name) => {
    const cookie = name === "referrerUrl" ? getReferrerUrl() : getCookie(name);
    if (isUsableAttributionValue(cookie)) {
      writeSession(sessionKeyFor(name), cookie);
      return;
    }
    const sealed = getSealedAttributionValue(name);
    if (sealed) writeCookie(name, sealed);
  });
};

/**
 * Writes an affiliate touch back as the attribution of this session: utm_*
 * (cookie + seal) and the entry page that carries the click id. The other
 * short-lived params are left alone — a direct return has none.
 */
const restoreAffiliateTouch = (touch: AffiliateTouch): void => {
  AFFILIATE_UTM_KEYS.forEach((key) => {
    const value = touch.utm[key];
    if (!value) return;
    writeSession(sessionKeyFor(key), value);
    writeCookie(key, value);
  });
  writeSession(SESSION_ENTRY_PAGE_KEY, touch.entryPage);
  setCookie("entryPage", touch.entryPage);
};

export const captureAttribution = (): void => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  sealLandingValues();

  const query = new URLSearchParams(window.location.search);
  const hasNewUTMs = ATTRIBUTION_PARAMS.some((param) => query.has(param));

  if (hasNewUTMs) {
    const threeMonthsDate = new Date(
      new Date().setMonth(new Date().getMonth() + 3)
    );

    ATTRIBUTION_PARAMS.forEach((param: string) => {
      const queryValue = query.get(param);

      if (LONG_LIVED_PARAMS.includes(param)) {
        if (queryValue) {
          writeSession(sessionKeyFor(param), queryValue);
          return recoverParam(query, param, threeMonthsDate);
        }
        // Absent from this url: the previous click id (cookie AND seal) stands.
        return;
      }

      // A new campaign resets the short lived params, cookie and seal alike —
      // otherwise the seal would resurrect the previous campaign's utm_* on the
      // next `syncAttributionSeal`.
      if (isUsableAttributionValue(queryValue)) {
        writeSession(sessionKeyFor(param), queryValue);
      } else {
        removeSession(sessionKeyFor(param));
      }
      recoverParam(query, param);
    });

    saveNewLandingValues();
  } else {
    restoreEntryPageCookie();
    restoreReferrerUrlCookie();
  }

  // Affiliate 30-day window (2026-09-24): save the affiliate arrival, clear it
  // on any other new campaign (last click), or bring it back on a direct return
  // once the 6-hour utm cookies are gone. See affiliateTouch.ts.
  applyAffiliateTouch({
    isNewCampaign: hasNewUTMs,
    hasLiveSource: isUsableAttributionValue(getAttributionValue("utm_source")),
    restore: restoreAffiliateTouch,
  });

  syncAttributionSeal();

  const DOMAIN = process.env.GATSBY_APP_LOCALE;
  setCookie(
    "gtf_campaign",
    DOMAIN === "es" || DOMAIN === "axa_wl_es" ? "3MESES1E" : null
  );
};

export const installAttributionCapture = (): void => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  captureAttribution();

  // Module level flag so HMR or a double `onClientEntry` never registers the
  // listener twice.
  if (attributionCaptureInstalled) return;
  attributionCaptureInstalled = true;

  // CookieYes deletes the attribution cookies while consent is pending; it
  // fires this event once the user interacts with the banner, which is our only
  // chance to write them back. `captureAttribution` -> `syncAttributionSeal`
  // restores every key from the seal, not just entryPage/referrerUrl.
  document.addEventListener("cookieyes_consent_update", () =>
    captureAttribution()
  );
};
